import cx from 'classnames';
import React, { ReactNode, useMemo, useRef } from 'react';
import { Mousewheel, Pagination, Swiper as SwiperApi } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Theme } from '../../constants/config';
import { ROUTES } from '../../constants/routes';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { LinkButton } from '../Button/LinkButton';
import { Reveal } from '../Reveal/Reveal';
import { RevealText } from '../RevealText/RevealText';
import { ScrollDownButton } from '../ScrollDownButton/ScrollDownButton';
import styles from './About.module.scss';
import { AboutView } from './AboutView';

interface AboutItem {
  nextLabel?: string;
  tag?: string;
  theme?: Theme;
  mobileLength?: number;
}

export interface AboutProps {
  items?: AboutItem[];
  children: ReactNode;
  indexState: [number, React.Dispatch<React.SetStateAction<number>>];
}

export const About = ({
  children,
  items: defaultItems = [],
  indexState,
}: AboutProps) => {
  const { isDesktop } = useAppContext();
  const [index, SetIndex] = indexState;
  const api = useRef<SwiperApi | null>(null);
  const onNext = () => api.current?.slideNext();

  const items = useMemo(() => {
    if (isDesktop) return defaultItems;
    return defaultItems.reduce((prev, curr) => {
      if (!curr.mobileLength) {
        prev.push(curr);
      } else {
        [...new Array(curr.mobileLength)].forEach(() => {
          prev.push(curr);
        });
      }
      return prev;
    }, [] as AboutItem[]);
  }, [defaultItems, isDesktop]);
  const item = items[index];
  const theme: Theme = item?.theme || 'light';
  const isLast = index === items?.length;

  return (
    <main className={cx(styles.about, theme)}>
      <Swiper
        direction="vertical"
        slidesPerView={1}
        speed={750}
        mousewheel
        pagination={{ clickable: true }}
        modules={[Mousewheel, Pagination]}
        className={styles.swiper}
        onInit={(event) => (api.current = event)}
        onTransitionStart={(event) => SetIndex(event.activeIndex)}
      >
        {children}
        <SwiperSlide>
          <AboutView
            mobileAlignItems="center"
            className={styles.helpUs}
            maxWidth={1024}
          >
            <RevealText
              delay={300}
              when={isLast}
              variant="headline-l-bold"
              desktopVariant="headline-xl-bold"
              as="h2"
            >
              We’re all in this together.
            </RevealText>
            <RevealText delay={400} when={isLast} variant="headline-s" as="p">
              With your help, we can fight fires, and fund the future.
            </RevealText>
            <Reveal when={isLast} delay={500} className={styles.buttonReveal}>
              <LinkButton variant="primary" href={ROUTES.MAP}>
                START FIGHTING 🔥
              </LinkButton>
            </Reveal>
          </AboutView>
        </SwiperSlide>
      </Swiper>
      {item?.nextLabel && (
        <Reveal when delay={400} className={styles.down}>
          <ScrollDownButton onClick={onNext} tag={item?.tag}>
            {item.nextLabel}
          </ScrollDownButton>
        </Reveal>
      )}
    </main>
  );
};
